import { intersection } from 'lodash';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { EmployeeUnitPosition } from '~/data/employee';
import { useAllUnitPositions } from '~/data/unitPositions/useAllUnitPositions';
import { getTranslationKeyForObjectKey } from '~/features/ScheduledEmploymentChanges/helpers';
import {
  EmploymentUpcomingChangeDifference,
  UpcomingChangeValueFormatter,
  useResolveUpcomingChanges,
} from '~/features/ScheduledEmploymentChanges/ScheduleChanges/components/Common/EmploymentUpcomingChanges';
import {
  DataPairingProcessPayload,
  PairedDataItem,
  useDataPairingForComparison,
} from '~/features/ScheduledEmploymentChanges/ScheduleChanges/components/ScheduleEmployment/data/useDataPairingForComparison';

enum ObjectKeyTranslation_UnitPositions {
  'fEmpId' = 'fEmpId',
  'fEmpUnitPos' = 'laOrgPosition',
  'fEmpUnitPosPercent' = 'laPercUnitPosition',
}

export const useUpcomingUnitPositionChanges = () => {
  const { t } = useTranslation();
  const { resolveUpcomingChanges, isLoading: isLoadingUpcomingResolvers } =
    useResolveUpcomingChanges();
  const { resolveDataItemPair, resolveRestDataItemPairs } =
    useDataPairingForComparison();

  const { unitPositions, isLoading: isLoadingUnitPositions } =
    useAllUnitPositions();
  const getUnitPositionName = useCallback(
    (upId: number): string => {
      if (upId < 0) {
        return '';
      }
      return (
        unitPositions.find((up) => up.fUnitPositionId === upId)
          ?.fUnitPositionName || upId.toString()
      );
    },
    [unitPositions],
  );

  const translateFieldName_UnitPosition = useCallback(
    (key: string): string => {
      const translationKey = getTranslationKeyForObjectKey<
        typeof ObjectKeyTranslation_UnitPositions
      >(key, ObjectKeyTranslation_UnitPositions);

      return t(translationKey);
    },
    [t],
  );

  const formatFieldValue_UnitPosition: UpcomingChangeValueFormatter =
    useCallback(
      (id: string, value?: string | number | boolean): string => {
        if (typeof value === 'undefined') {
          return '';
        }

        switch (id) {
          case 'fEmpUnitPos':
            return typeof value === 'number' ? getUnitPositionName(value) : '';
          case 'fEmpUnitPosPercent':
            return `${!!value ? value : 0}%`;
          default: {
            return `${value}`;
          }
        }
      },
      [getUnitPositionName],
    );

  const resolveUnitPositionPairing = useCallback(
    (oldArray: EmployeeUnitPosition[], newArray: EmployeeUnitPosition[]) => {
      const emptyUnitPosition: EmployeeUnitPosition = {
        fEmpId: -1,
        fEmpUnitPos: -1,
        fEmpUnitPosPercent: 0,
      };

      const unitPositionPairing = (
        props: DataPairingProcessPayload<EmployeeUnitPosition>,
      ): PairedDataItem<EmployeeUnitPosition>[] => {
        const resolveIdPairs = (
          p: DataPairingProcessPayload<EmployeeUnitPosition>,
        ): DataPairingProcessPayload<EmployeeUnitPosition> => {
          const getUPIds = (array: EmployeeUnitPosition[]) => {
            return array.map((up) => up.fEmpUnitPos);
          };

          const oldIds = getUPIds(p.unprocessed.oldArray);
          const newIds = getUPIds(p.unprocessed.newArray);

          // Process items with common id's
          return intersection(oldIds, newIds).reduce(
            (
              lastPayload: DataPairingProcessPayload<EmployeeUnitPosition>,
              upId,
            ) => {
              const getIndexById = (upArray: EmployeeUnitPosition[]) =>
                upArray.findIndex((up) => up.fEmpUnitPos === upId);

              const { payload } = resolveDataItemPair(
                lastPayload,
                getIndexById,
                emptyUnitPosition,
              );

              return payload;
            },
            p,
          );
        };

        const { paired } = resolveRestDataItemPairs(
          resolveIdPairs(props),
          emptyUnitPosition,
        );

        return paired;
      };

      return unitPositionPairing({
        unprocessed: {
          oldArray,
          newArray,
        },
        paired: [],
        checkList: {},
      });
    },
    [resolveDataItemPair, resolveRestDataItemPairs],
  );

  const resolveUpcomingUnitPositionChanges = useCallback(
    (
      oldData: EmployeeUnitPosition[],
      newData: EmployeeUnitPosition[],
    ): EmploymentUpcomingChangeDifference[] => {
      return resolveUnitPositionPairing(oldData, newData)
        .map((upData) => {
          return resolveUpcomingChanges({
            standardFields: {
              fieldKeys: Object.keys(ObjectKeyTranslation_UnitPositions),
              fieldKeyExceptions: ['fEmpId'],
              ...upData,
              valueFormatter: formatFieldValue_UnitPosition,
              labelTranslator: translateFieldName_UnitPosition,
            },
          }).map((diff): EmploymentUpcomingChangeDifference => {
            return {
              groupLabel: `${t('laOrgPosition')} (${getUnitPositionName(
                upData.newData.fEmpUnitPos > 0
                  ? upData.newData.fEmpUnitPos
                  : upData.oldData.fEmpUnitPos,
              )})`,
              ...diff,
            };
          });
        })
        .flat();
    },
    [
      t,
      getUnitPositionName,
      resolveUnitPositionPairing,
      resolveUpcomingChanges,
      formatFieldValue_UnitPosition,
      translateFieldName_UnitPosition,
    ],
  );

  return {
    resolveUpcomingUnitPositionChanges,
    isLoading: isLoadingUnitPositions || isLoadingUpcomingResolvers,
  };
};
